import api from './api';

// Feature roadmap client (Jaime-approved MVP, 2026-10-12/13).
// Endpoints (src/modules/feature-requests/routes.js):
//   GET  /api/public/feature-roadmap        — no auth; whitelist fields only
//   GET  /api/feature-requests/my-votes     — authed; voted feature ids
//   POST /api/feature-requests/:id/vote     — authed toggle -> { voted, vote_count }
//   GET  /api/feature-requests/:id/comments — authed; id/body/created_at/author_name
//   POST /api/feature-requests/:id/comments — authed; body 1..2000 chars after trim
// 503 FEATURE_ROADMAP_UNAVAILABLE = migration 161/162 not applied on the env.

export interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  area: string;
  status: 'planned' | 'in_progress' | 'shipped';
  eta_label: string | null;
  vote_count: number;
  comment_count: number;
  credit_label?: string;
}

export interface RoadmapComment {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null;
}

export interface RoadmapVoteResult {
  voted: boolean;
  vote_count: number;
}

// Shared error extractor: prefers the backend human message (already
// Spanish: validation copy, rate-limit copy, no-leak 404s).
export function roadmapErrorMessage(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: string } }; message?: string };
  return err?.response?.data?.error || fallback;
}

export function roadmapErrorStatus(e: unknown): number | null {
  const err = e as { response?: { status?: number } };
  return typeof err?.response?.status === 'number' ? err.response.status : null;
}

export const roadmapApi = {
  async list(): Promise<RoadmapItem[]> {
    const res = await api.get('/public/feature-roadmap');
    const data = res.data?.data;
    return Array.isArray(data) ? (data as RoadmapItem[]) : [];
  },

  async myVotes(): Promise<string[]> {
    const res = await api.get('/feature-requests/my-votes');
    const data = res.data?.data;
    return Array.isArray(data) ? (data as string[]) : [];
  },

  async vote(id: string): Promise<RoadmapVoteResult> {
    const res = await api.post(`/feature-requests/${id}/vote`);
    return (res.data?.data || { voted: false, vote_count: 0 }) as RoadmapVoteResult;
  },

  async comments(id: string): Promise<RoadmapComment[]> {
    const res = await api.get(`/feature-requests/${id}/comments`);
    const data = res.data?.data;
    return Array.isArray(data) ? (data as RoadmapComment[]) : [];
  },

  async addComment(id: string, body: string): Promise<RoadmapComment> {
    const res = await api.post(`/feature-requests/${id}/comments`, { body });
    return res.data?.data as RoadmapComment;
  },
};
